/**
 * Filter interaction tracking for PostHog.
 *
 * One event name, `verb_filter`, with the interaction described by its properties:
 *
 *     label       the filter's label, e.g. "Region"
 *     event_type  clicked | selected | focus | blur
 *     value       what the control holds or was given, serialized to a string
 *     type        select | date picker | input
 *
 * The super properties registered in common/utils/load-posthog.js (iframe key, collection)
 * ride along automatically, so nothing here needs to repeat them.
 *
 * No-ops when PostHog is absent, which is the case outside the SDK iframe.
 */

export const filterEventTypes = {
    clicked: "clicked",
    selected: "selected",
    focus: "focus",
    blur: "blur",
};

export const filterControlTypes = {
    select: "select",
    datePicker: "date picker",
    input: "input",
};

const MAX_VALUE_LENGTH = 200;
const MAX_ARRAY_ITEMS = 20;

const isDateObject = (v) => v != null && typeof v === "object" && "day" in v && "month" in v && "year" in v;

const truncate = (str) => (str.length > MAX_VALUE_LENGTH ? `${str.slice(0, MAX_VALUE_LENGTH)}...` : str);

// labels and values reaching here can be react elements (a styled placeholder, a chip),
// date objects, or arrays of selections. flatten all of it to something PostHog can group on.
export const serializeFilterValue = (value) => {
    if (value == null) {
        return null;
    }

    if (Array.isArray(value)) {
        const items = value.slice(0, MAX_ARRAY_ITEMS).map(serializeFilterValue).filter((v) => v != null);
        const suffix = value.length > MAX_ARRAY_ITEMS ? ` +${value.length - MAX_ARRAY_ITEMS} more` : "";
        return items.length ? truncate(items.join(", ") + suffix) : null;
    }

    if (typeof value === "object") {
        // react element, nothing useful to send
        if (value.$$typeof) {
            return null;
        }

        if (isDateObject(value)) {
            const pad = (n) => String(n).padStart(2, "0");
            return `${pad(value.month)}/${pad(value.day)}/${value.year}`;
        }

        try {
            return truncate(JSON.stringify(value));
        } catch (e) {
            return null;
        }
    }

    return truncate(String(value));
};

export const trackFilterEvent = ({label, eventType, value, type}) => {
    window.posthog?.capture("verb_filter", {
        verb_filter_label: serializeFilterValue(label),
        verb_filter_event_type: eventType,
        verb_filter_value: serializeFilterValue(value),
        verb_filter_type: type,
    });
};
